import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { MassMessaging, MassMessagingStatusEnum } from 'src/app/marketing/shared';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { SiteV2 } from 'src/app/shared/models';
import { API_URL_COMPANIES, BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { WhatsappService } from 'src/app/shared/strategies/channels';

@Injectable({
  providedIn: 'root',
})
export class MassMessagingService {
  REFRESH_DELAY_MS = 60000;
  isRunning = false;
  autoRefreshSubscription = new Subscription();
  campaignScenarios = [];
  segments = [];
  tabIndex = 0;
  dataSource = new MatTableDataSource();
  subscriptions: Subscription = new Subscription();

  constructor(
    private httpClient: HttpClient,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private sitesService: SitesService,
    private whatsappService: WhatsappService
  ) {}

  getCampaigns(): Observable<MassMessaging[]> {
    return this.httpClient.get<MassMessaging[]>(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/mass-messaging`
    );
  }

  getCampaign(id: number): Observable<MassMessaging> {
    return this.httpClient
      .get<MassMessaging>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/mass-messaging/${id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError(), map(this.formatCampaign));
  }

  private formatCampaign = (campaign: MassMessaging): MassMessaging => {
    const { creator_admin_user_id, updater_admin_user_id, starter_admin_user_id, isScheduled } = campaign;

    const defaultLabel = this.basicService.translate('admin/users/is_admin');
    const getColleagueName = (id: number) => this.alcmeonContextService.getColleague(id)?.realname ?? defaultLabel;

    const createdByLabel = getColleagueName(creator_admin_user_id);
    const modifiedByLabel = getColleagueName(updater_admin_user_id);
    const sentByLabel = getColleagueName(starter_admin_user_id);
    const scheduledLabel = this.basicService.translate(isScheduled ? 'yes' : 'no');

    return {
      ...campaign,
      createdByLabel,
      modifiedByLabel,
      sentByLabel,
      scheduledLabel,
    };
  };

  getMassMessagingVolumes(massMessaging: MassMessaging) {
    return this.httpClient.get(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/mass-messaging/${massMessaging.id}/volumes`
    );
  }

  deleteMassMessaging(massMessaging) {
    return this.httpClient.delete(
      `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/mass-messaging/${massMessaging.id}`
    );
  }

  getStatusLabel = (massMessaging: MassMessaging) => {
    let status = null;
    switch (massMessaging.status) {
      case MassMessagingStatusEnum.CREATED_STATUS:
        status = 'created';
        break;
      case MassMessagingStatusEnum.STARTED_STATUS:
        status = 'started';
        break;
      case MassMessagingStatusEnum.COMPLETED_STATUS:
        status = 'completed';
        break;
      case MassMessagingStatusEnum.CANCELLED_STATUS:
        status = 'cancelled';
        break;
      case MassMessagingStatusEnum.ON_HOLD_STATUS:
        status = 'on_hold';
        break;
      case MassMessagingStatusEnum.WAITING_FOR_QUOTA:
        status = 'waiting_for_quota';
        break;
    }

    return this.basicService.translate(`mass_messaging/${status}_status`);
  };

  getDataFromAudienceFilters(campaign: MassMessaging) {
    const { audience_filters } = campaign;
    let read: string;
    let account: SiteV2;
    if (audience_filters) {
      account = this.sitesService.sitesV2Map.get(+audience_filters.accounts[0]);
      if (account?.type !== this.whatsappService.getType()) {
        read = this.basicService.translate('not_applicable');
      }
    }
    return { read, account };
  }

  resendForUserInError(id: number) {
    return this.httpClient
      .post(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/mass-messaging/${id}/resend-for-users-in-error`,
        {}
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
